import { useState } from "react";
import type { AnalyzedItem } from "@gull/shared";
import { MAX_ITEMS } from "../constants";
import { emptyDraftItem, type DraftItem } from "../types";

export function useDraftItems() {
  const [items, setItems] = useState<DraftItem[]>([]);

  const updateItem = (index: number, description: string) =>
    setItems((prev) => prev.map((item, i) => (i === index ? { ...item, description } : item)));

  const addItem = () =>
    setItems((prev) => (prev.length >= MAX_ITEMS ? prev : [...prev, emptyDraftItem()]));

  const removeItem = (index: number) => setItems((prev) => prev.filter((_, i) => i !== index));

  const startManualEntry = () => setItems([emptyDraftItem()]);

  const restore = (restored: DraftItem[]) => setItems(restored);

  const mergeAnalyzedItems = (analyzed: AnalyzedItem[]) => {
    const labels = analyzed
      .flatMap((item) => {
        const label = item.label?.trim();
        if (label) return [label];
        return (
          item.components?.map((component) => component.description?.trim()).filter(Boolean) ?? []
        );
      })
      .filter((label): label is string => !!label);
    if (labels.length === 0) return;
    setItems((prev) => {
      const existing = new Set(
        prev.map((item) => item.description.trim().toLowerCase()).filter(Boolean),
      );
      const suggestions = labels
        .filter((label) => !existing.has(label.toLowerCase()))
        .map((description) => ({ description, source: "ai" as const }));
      if (suggestions.length === 0) return prev;
      if (prev.length === 1 && !prev[0]?.description.trim()) return suggestions;
      return [...prev, ...suggestions];
    });
  };

  const allDescribed = items.length > 0 && items.every((item) => item.description.trim());

  return {
    items,
    updateItem,
    addItem,
    removeItem,
    startManualEntry,
    restore,
    mergeAnalyzedItems,
    allDescribed,
    atLimit: items.length >= MAX_ITEMS,
  };
}

export type DraftItems = ReturnType<typeof useDraftItems>;
